import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { theme } from "../styles/theme";
import { Modal } from "./ui";
import { useAuth } from "../hooks/useAuth";
import { useAdminAuth } from "../hooks/useAdmin";
import { QRCodeSVG } from "qrcode.react";

const HEADER_APP_STORE_URL = "https://apps.apple.com/app/id6758546772";
const HEADER_PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.fourkmatchapp";

interface HeaderProps {
  onNavigate?: (section: string) => void;
  onNavigateToLogin?: () => void;
}

/** Fixed navigation with secondary account actions and the existing app modal. */
export const Header: React.FC<HeaderProps> = ({ onNavigate, onNavigateToLogin }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isAppModalOpen, setIsAppModalOpen] = useState(false);
  const headerRef = useRef<HTMLElement>(null);
  const menuRef = useRef<HTMLElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const logoRef = useRef<HTMLButtonElement>(null);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: isAdmin } = useAdminAuth();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (!isMobileMenuOpen) return;
    menuRef.current?.querySelector<HTMLButtonElement>("button")?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        setIsMobileMenuOpen(false);
        menuButtonRef.current?.focus();
      }
    };
    const onPointerDown = (event: PointerEvent) => {
      if (!headerRef.current?.contains(event.target as Node)) setIsMobileMenuOpen(false);
    };
    const desktop = window.matchMedia("(min-width: 1101px)");
    const onDesktop = () => {
      if (desktop.matches) {
        if (menuRef.current?.contains(document.activeElement)) logoRef.current?.focus();
        setIsMobileMenuOpen(false);
      }
    };
    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("pointerdown", onPointerDown);
    desktop.addEventListener("change", onDesktop);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("pointerdown", onPointerDown);
      desktop.removeEventListener("change", onDesktop);
    };
  }, [isMobileMenuOpen]);

  const navItems = [
    { label: "How it works", id: "how-it-works" },
    { label: "Clubs", id: "features" },
    { label: "Pricing", id: "pricing" },
  ];
  // This project's older DOM types omit "instant", which bypasses CSS smooth scrolling.
  const scrollBehavior = (): ScrollBehavior =>
    window.matchMedia("(prefers-reduced-motion: reduce)").matches ? ("instant" as ScrollBehavior) : "smooth";
  const closeMenu = () => {
    if (isMobileMenuOpen) menuButtonRef.current?.focus();
    setIsMobileMenuOpen(false);
  };
  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    closeMenu();
    if (element) {
      const headerHeight = headerRef.current?.getBoundingClientRect().height ?? 68;
      const existingOffset = parseFloat(window.getComputedStyle(element).scrollMarginTop) || 0;
      // Explicit coordinates bypass scroll-margin; use the larger offset, not their sum.
      const offset = Math.max(headerHeight + 12, existingOffset);
      window.scrollTo({ top: Math.max(0, window.scrollY + element.getBoundingClientRect().top - offset), behavior: scrollBehavior() });
    }
    onNavigate?.(id);
  };
  const openAppModal = () => {
    closeMenu();
    setIsAppModalOpen(true);
  };

  return (
    <>
      <header
        ref={headerRef}
        className={`site-header${isScrolled ? " site-header-scrolled" : ""}`}
        onBlur={(event) => {
          if (event.relatedTarget && !event.currentTarget.contains(event.relatedTarget as Node)) setIsMobileMenuOpen(false);
        }}
      >
        <div className={`header-inner${user ? " header-inner-authenticated" : ""}`}>
          <button ref={logoRef} type="button" className="header-logo" aria-label="4KMATCH home" onClick={() => { closeMenu(); window.scrollTo({ top: 0, behavior: scrollBehavior() }); }}>
            <img className="header-logo-image" src="/images/4kmatch-header-gradient.png" alt="" width="2943" height="439" />
          </button>

          <nav className="desktop-nav" aria-label="Main navigation">
            {navItems.map((item) => <button type="button" key={item.id} className="header-secondary" onClick={() => scrollToSection(item.id)}>{item.label}</button>)}
          </nav>

          <div className="desktop-cta">
            {user && isAdmin && <button type="button" className="header-secondary" onClick={() => navigate("/admin")}>Admin</button>}
            {user && <button type="button" className="header-secondary" onClick={() => navigate("/wallet")}>Wallet</button>}
            {!user && <button type="button" className="header-secondary" onClick={onNavigateToLogin}>Sign in</button>}
            <button type="button" className="header-app" onClick={openAppModal}>Get the app</button>
          </div>

          <div className="header-mobile-actions">
            <button type="button" className="header-app header-mobile-app" onClick={openAppModal}>Get the app</button>
            <button
              ref={menuButtonRef}
              type="button"
              className="mobile-menu-btn"
              aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
              aria-expanded={isMobileMenuOpen}
              aria-controls="header-mobile-menu"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                <path d={isMobileMenuOpen ? "M6 6l12 12M6 18 18 6" : "M4 6h16M4 12h16M4 18h16"} />
              </svg>
            </button>
          </div>
        </div>

        {/* Non-modal disclosure: normal Tab navigation can leave and close it. */}
        <nav id="header-mobile-menu" ref={menuRef} className="header-mobile-menu" aria-label="Mobile navigation" hidden={!isMobileMenuOpen}>
          {navItems.map((item) => <button type="button" key={item.id} className="header-secondary" onClick={() => scrollToSection(item.id)}>{item.label}</button>)}
          <div className="header-mobile-account">
            {user && isAdmin && <button type="button" className="header-secondary" onClick={() => { closeMenu(); navigate("/admin"); }}>Admin</button>}
            {user && <button type="button" className="header-secondary" onClick={() => { closeMenu(); navigate("/wallet"); }}>Wallet</button>}
            {!user && <button type="button" className="header-secondary" onClick={() => { closeMenu(); onNavigateToLogin?.(); }}>Sign in</button>}
          </div>
          <button type="button" className="header-app" onClick={openAppModal}>Get the app</button>
        </nav>
      </header>

      <style>{`
        .site-header { position: fixed; inset: 0 0 auto; z-index: ${theme.zIndex.sticky}; padding: 11px 24px; box-sizing: border-box; background: rgba(0,0,0,.82); border-bottom: 1px solid transparent; font-family: ${theme.fonts.primary}; transition: background-color .2s ease, border-color .2s ease, backdrop-filter .2s ease; }
        .site-header-scrolled { background: rgba(10,10,11,.95); backdrop-filter: blur(20px); border-bottom-color: ${theme.colors.border}; }
        .site-header .header-inner { max-width: 1120px; margin: 0 auto; display: flex; align-items: center; justify-content: space-between; gap: 20px; min-width: 0; }
        .site-header .header-inner-authenticated { max-width: 1240px; }
        .site-header button { font-family: inherit; font-size: 16px; font-weight: 500; line-height: 1.25; min-height: 44px; cursor: pointer; white-space: nowrap; }
        .site-header .header-logo { display: flex; align-items: center; padding: 0; background: transparent; border: 0; flex-shrink: 1; min-width: 0; }
        .site-header .header-logo-image { display: block; width: 224px; max-width: 100%; height: auto; }
        .site-header .desktop-nav, .site-header .desktop-cta { display: flex; align-items: center; gap: 16px; }
        .site-header .header-secondary { color: #b8bdc6; background: transparent; border: 0; border-radius: 8px; padding: 10px 8px; transition: color .2s ease, background-color .2s ease; }
        .site-header .header-secondary:hover { color: #fff; background: rgba(255,255,255,.05); }
        .site-header .header-app { display: inline-flex; justify-content: center; align-items: center; padding: 11px 20px; border: 1px solid transparent; border-radius: 24px; background: ${theme.colors.gradientPrimary}; color: #fff; font-weight: 600; transition: filter .2s ease; }
        .site-header .header-app:hover { filter: brightness(1.12); }
        .site-header button:focus-visible { outline: 2px solid #fff; outline-offset: 3px; }
        .site-header .header-mobile-actions { display: none; align-items: center; gap: 16px; }
        .site-header .mobile-menu-btn { display: flex; justify-content: center; align-items: center; width: 44px; padding: 10px; border: 1px solid #333; border-radius: 10px; background: transparent; color: #fff; }
        .site-header .header-mobile-menu { position: absolute; top: 100%; left: 16px; right: 16px; padding: 12px; border: 1px solid #333; border-radius: 14px; background: #0a0a0b; display: flex; flex-direction: column; gap: 4px; max-height: calc(100dvh - 84px); overflow-y: auto; box-sizing: border-box; }
        .site-header .header-mobile-menu[hidden] { display: none; }
        .site-header .header-mobile-menu .header-secondary { text-align: left; white-space: normal; }
        .site-header .header-mobile-account { display: flex; flex-wrap: wrap; gap: 12px; padding-top: 4px; margin-top: 4px; border-top: 1px solid #252525; }
        .site-header .header-mobile-menu > .header-app { margin-top: 8px; }
        @media (min-width: 1101px) { .site-header .header-mobile-menu { display: none; } }
        @media (max-width: 1100px) {
          .site-header .desktop-nav, .site-header .desktop-cta { display: none; }
          .site-header .header-mobile-actions { display: flex; }
        }
        @media (max-width: 620px) {
          .site-header { padding-left: 16px; padding-right: 16px; }
          .site-header .header-inner { gap: 12px; }
          .site-header .header-mobile-app { display: none; }
        }
        @media (prefers-reduced-motion: reduce) {
          .site-header, .site-header button { transition: none; }
        }
      `}</style>

      {/* App Download Modal */}
      <Modal
        isOpen={isAppModalOpen}
        onClose={() => setIsAppModalOpen(false)}
        title="Download the App"
      >
        <div style={{ textAlign: "center" }}>
          <p
            style={{
              fontSize: theme.fontSizes.sm,
              color: theme.colors.textMuted,
              marginBottom: theme.spacing.lg,
            }}
          >
            Scan the QR code or click a button below
          </p>

          {/* QR Code */}
          <div
            style={{
              background: "white",
              padding: theme.spacing.lg,
              borderRadius: theme.borderRadius.lg,
              display: "inline-block",
              marginBottom: theme.spacing.lg,
            }}
          >
            <QRCodeSVG
              value={HEADER_APP_STORE_URL}
              size={160}
              level="M"
              fgColor="#000000"
              bgColor="#FFFFFF"
            />
          </div>

          {/* App Store Buttons */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: theme.spacing.sm,
            }}
          >
            <a
              href={HEADER_APP_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: theme.spacing.sm,
                padding: theme.spacing.md,
                background: "#000",
                borderRadius: theme.borderRadius.md,
                color: "white",
                textDecoration: "none",
                fontSize: theme.fontSizes.sm,
                fontWeight: 500,
                transition: `all ${theme.transitions.fast}`,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.opacity = "0.9";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.opacity = "1";
              }}
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
              </svg>
              <div style={{ textAlign: "left" }}>
                <div style={{ fontSize: theme.fontSizes.xs, opacity: 0.8 }}>
                  Download on the
                </div>
                <div style={{ fontWeight: 600 }}>App Store</div>
              </div>
            </a>

            <a
              href={HEADER_PLAY_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: theme.spacing.sm,
                padding: theme.spacing.md,
                background: "#000",
                borderRadius: theme.borderRadius.md,
                color: "white",
                textDecoration: "none",
                fontSize: theme.fontSizes.sm,
                fontWeight: 500,
                transition: `all ${theme.transitions.fast}`,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.opacity = "0.9";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.opacity = "1";
              }}
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z" />
              </svg>
              <div style={{ textAlign: "left" }}>
                <div style={{ fontSize: theme.fontSizes.xs, opacity: 0.8 }}>
                  Get it on
                </div>
                <div style={{ fontWeight: 600 }}>Google Play</div>
              </div>
            </a>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default Header;
